import { ChangeService } from "@/@types/changeservice";
import { GetRequest, PatchRequest, PostRequest, PutRequest } from "./RequestTemplates";
import axios from "axios";

export const createChangeService = (userId: string, changeService: Record<string, string | boolean>) => {
  return PostRequest(`${process.env.API_SERVER_URL}/users/${userId}/change-services`, changeService);
};

export const updateChangeService = (userId: string, csId: string, changeService: Partial<ChangeService>) => {
  return PutRequest(`${process.env.API_SERVER_URL}/users/${userId}/change-services/${csId}`, changeService).catch(
    (err) => err?.response
  );
};

export const getChangeServices = (userId: string) => {
  return GetRequest(`${process.env.API_SERVER_URL}/users/${userId}/change-services`).then((res) => {
    if (res.status === 200) {
      return res.data;
    }
  });
};

export const getSingleChangeService = (userId: string, changeServiceId: string) => {
  return GetRequest(`${process.env.API_SERVER_URL}/users/${userId}/change-services/${changeServiceId}`).then((res) => {
    if (res.status === 200) {
      return res.data;
    }
  });
};

export const getChangeServiceStatus = (id: string) => {
  return GetRequest(`${process.env.CHANGE_SERVICE_STATUS_URL}/change-service-status/${id}`)
    .then((res) => {
      if (res.status === 200) {
        return res.data;
      }
    })
    .catch((e) => {
      console.log(e);
    });
};

export const getTariffHistory = (id: string) => {
  return GetRequest(`${process.env.API_SERVER_URL}/tariff/change-service/${id}`).then((res) => {
    if (res.status === 200) {
      return res.data;
    }
  });
};

const downloadDocumentUrl = `${process.env.API_SERVER_URL}/doc-manager/document/view/`;

export const getDocument = (docId: string) => {
  return GetRequest(downloadDocumentUrl + docId + "?base64=true").then((res) => {
    if (res.status === 200) {
      return res.data;
    }
  });
};

export const getDocuments = (id: string) => {
  return GetRequest(`${process.env.API_SERVER_URL}/doc-manager/documents?remoteId=${id}`).then((res) => {
    if (res.status === 200) {
      if (Array.isArray(res.data) && res.data.length > 0) {
        const promises = res.data.map(async (item) => {
          return await GetRequest(downloadDocumentUrl + item.id + "?base64=true").then((url) => {
            item.fileDownloadUrl = url;
            return item;
          });
        });

        return Promise.all(promises).then((urls) => {
          const onlyPDFs = urls.filter((doc) => {
            return doc.contentType === "application/pdf" && doc.tags?.includes("VISIBILITY_USER");
          });
          return onlyPDFs;
        });
      } else {
        return Promise.resolve([]);
      }
    }
  });
};

interface DeactivationValues {
  changeServiceId: string;
  feedbackContent: {
    selectedOption: string;
    text: string;
  };
  type: string;
  userId: string;
}

export const deactivateChangeService = (deactivationValues: DeactivationValues) => {
  return PostRequest(`${process.env.CHANGE_SERVICE_DEACTIVATION_SERVER_URL}`, deactivationValues, undefined, {
    "x-api-key": `${process.env.CHANGE_SERVICE_DEACTIVATION_KEY}`,
  });
};

export const updateIban = (csId: string, ibanData: Record<string, string>) => {
  return PatchRequest(`${process.env.API_SERVER_URL}/change-services/${csId}/iban`, ibanData);
};

const sleepingCustomerUrl = `${process.env.API_MISSING_DATA_SERVER_URL}/missing-data-provision`;

export const getSleepingCustomerFields = (tariffId: string) => {
  const url = `${sleepingCustomerUrl}/${tariffId}`;
  const request = axios.create();

  return request
    .get(url, {
      headers: {
        Accept: "application/json",
        "x-api-key": process.env.API_MISSING_DATA_SERVER_API_KEY || "",
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    })
    .catch((err) => {
      throw err;
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sanitize = (sleepingCustomer: any) =>
  Object.entries(sleepingCustomer).reduce(
    (values, [field, value]) => ({
      ...values,
      ...(value ? { [field]: value } : {}),
    }),
    {}
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateSleepingCustomerFields = (tariffId: string, values: any) => {
  const url = `${sleepingCustomerUrl}/${tariffId}`;
  const sanitized = sanitize(values);
  const valuesSent = JSON.stringify({ missingDataProvided: JSON.stringify(sanitized) });

  const request = axios.create();
  return request
    .put(url, valuesSent, {
      headers: {
        Accept: "application/json",
        "x-api-key": process.env.API_MISSING_DATA_SERVER_API_KEY || "",
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
      throw new Error("Failed to update with non-200 status");
    });
};

export const checkChangeServiceCounterNumber = (userId: string, counterNumber: string) => {
  return GetRequest(
    `${process.env.API_SERVER_URL}/users/${userId}/change-services/check-counter-number?counterNumber=${counterNumber}`
  ).then((res) => {
    if (res.status === 200) {
      return res.data;
    }
  });
};
